import type { RecipeVersionStatus } from './components/recipeTypes';

/**
 * The three questions the recipe detail screen asks that the API does not
 * answer directly, kept here so each one carries its reason.
 */

/**
 * `PATCH /recipes/versions/:versionId` is documented "Edit a DRAFT version —
 * published versions are immutable", and the service 400s anything else. The
 * screen never offers an edit the server would refuse; the immutability is
 * stated where the button would otherwise be.
 */
export function mayEditVersion(status: RecipeVersionStatus | string): boolean {
  return status === 'DRAFT';
}

/**
 * Cost of one unit of the version's yield — the design's "Cost per gram".
 *
 * `GET /recipes/:id/explode` returns `totalCost` and per-line costs only;
 * `unitCost` exists on `POST /recipes/cost-preview`, which takes an unsaved
 * BOM and so cannot answer for a published version. The division is therefore
 * client-side, and the screen says so.
 *
 * Four decimals because a per-gram ingredient cost sits under any currency's
 * minor unit — the same reason `Orders.tsx` widens `maximumFractionDigits` for
 * the COGS panel.
 */
export function costPerYieldUnit(explosion: {
  totalCost: string;
  quantity: string;
}): string | null {
  const total = Number(explosion.totalCost);
  const quantity = Number(explosion.quantity);
  if (!Number.isFinite(total) || !Number.isFinite(quantity) || quantity === 0) return null;
  return (total / quantity).toFixed(4);
}

/**
 * The one product a recipe is the bill of materials for.
 *
 * `Recipe.catalogItemId` is `@unique`, so "Used in" is at most a single row —
 * the design's two priced dishes are not a shape this model can produce. The
 * only many-relation is `usedAsSubRecipe`, which points at other **recipes**.
 */
export function usedInItem(recipe: {
  catalogItem: { id: string; name: string; basePrice: string } | null;
}): { id: string; name: string; basePrice: string } | null {
  return recipe.catalogItem ?? null;
}
